/**
 * PromoBannersTableV2 — Upgrade Banners list, redesign port of
 * `PromoBannersTable.js` (MUI sticky AppBar + DnD sortable rows).
 *
 * Layout: single LibTable section (no quick-tap / audio split like
 * Categories — promo banners are one flat ordered list). Columns:
 *   • Drag handle  — only the handle captures pointer for the sortable
 *   • Banner       — promo_banner_url thumbnail
 *   • Icon         — promo_banner_icon on a navy chip (matches the way
 *                    the app renders the banner icon over its dark BG)
 *   • Content      — promo_banner_title + promo_banner_text
 *   • Status       — StatusPill driven by promo_banner_is_active
 *   • Actions      — hover-revealed Edit
 *
 * Reordering reuses the existing `updatePromoBannerOrder` action.
 * Same payload shape as v1: array of promo_banner_ids in the new order.
 *
 * Data source: `state.banners.banners` via `fetchAllPromoBanners`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchAllPromoBanners,
  updatePromoBannerOrder,
} from '../../../actions/promoBannerActions';
import {
  LibTable,
  StatusPill,
  IconButton,
  cn,
} from '../../redesign';

/* ─── Icons (kept inline; brand pack has no drag-handle glyph) ──────── */

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────────── */

function sortBySortNumber(a, b) {
  return Number(a.promo_banner_sort_number ?? 0) - Number(b.promo_banner_sort_number ?? 0);
}

// URL guard for the banner image + icon. Treats null / undefined / empty /
// whitespace AND the literal "null" / "undefined" strings as missing —
// the legacy API occasionally hands back the string "null" for unset
// fields, which slips past a plain truthy check and renders a broken
// image / empty chip in the UI.
function hasUrl(value) {
  if (value == null) return false;
  const s = String(value).trim();
  if (s === '' || s === 'null' || s === 'undefined') return false;
  return true;
}

/* ─── IconChip ─────────────────────────────────────────────────────
 *  Renders the navy chip + composited icon glyph, but folds itself
 *  away entirely when the underlying URL fails to load (404, bad
 *  hostname, blocked content, etc.). Static URL checks miss those
 *  cases — onError is the only way to know the image is broken once
 *  it has hit the network.
 */
function IconChip({ url }) {
  // Defensive — empty / null / "null" URLs never even attempt a load.
  const [broken, setBroken] = useState(!hasUrl(url));

  // Reset broken-state when the URL changes (e.g. after a save).
  useEffect(() => {
    setBroken(!hasUrl(url));
  }, [url]);

  if (broken) {
    return (
      <span className="text-text-muted" style={{ fontSize: 11 }} aria-hidden>
        —
      </span>
    );
  }

  return (
    <div
      className="bg-navy inline-grid place-items-center"
      style={{
        width: 36,
        height: 36,
        borderRadius: 6,
        padding: 5,
      }}
    >
      <img
        src={url}
        alt=""
        onError={() => setBroken(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          filter: 'brightness(0) invert(1)',
        }}
      />
    </div>
  );
}

/* ─── Sortable row ─────────────────────────────────────────────────── */

function SortablePromoBannerRow({ banner }) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: banner.promo_banner_id });

  const inactive = !banner.promo_banner_is_active;

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 5 : undefined,
  };

  return (
    <LibTable.Row inactive={inactive} ref={setNodeRef} style={dragStyle} {...attributes}>
      {/* Drag handle cell — pointer listeners attached here only so the
       *  rest of the row stays clickable / hoverable. */}
      <td
        className="border-b border-line text-center group-data-[inactive=true]:bg-[var(--color-row-inactive)] group-data-[inactive=true]:group-hover:bg-[var(--color-row-inactive-hover)]"
        style={{ width: 36, padding: '10px 0 10px 10px' }}
      >
        <span
          {...listeners}
          aria-label="Drag to reorder"
          tabIndex={0}
          className={cn(
            'inline-grid place-items-center text-text-muted cursor-grab',
            'opacity-[0.35] group-hover:opacity-100',
            'hover:bg-cream hover:text-navy group-data-[inactive=true]:hover:bg-[var(--color-row-inactive-hover)] active:cursor-grabbing',
            'focus:outline-none focus-visible:bg-cream group-data-[inactive=true]:focus-visible:bg-[var(--color-row-inactive-hover)] focus-visible:text-navy',
          )}
          style={{
            width: 26,
            height: 26,
            borderRadius: 4,
            transition: 'all 140ms ease',
            touchAction: 'none',
          }}
        >
          <span style={{ width: 13, height: 13, display: 'inline-flex' }}>
            {DragHandleIcon}
          </span>
        </span>
      </td>

      {/* Banner thumbnail — 180×100 (about 9:5) so the banner artwork
       *  + on-banner copy is actually legible without opening the
       *  edit page. `contain` rather than `cover` so we don't crop
       *  text that lives near the edges. */}
      <LibTable.Td style={{ padding: '12px 14px' }}>
        {hasUrl(banner.promo_banner_url) ? (
          <div
            className="bg-cream border border-sand"
            style={{
              width: 180,
              height: 100,
              borderRadius: 6,
              backgroundImage: `url('${banner.promo_banner_url}')`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        ) : (
          <div
            className="bg-cream border border-sand grid place-items-center text-text-muted italic"
            style={{ width: 180, height: 100, borderRadius: 6, fontSize: 11 }}
          >
            no image
          </div>
        )}
      </LibTable.Td>

      {/* Icon — navy chip with the white glyph composited on top.
       *  When the URL is missing OR fails to load (onError), the
       *  IconChip folds itself away and renders an em-dash instead
       *  of a broken-image placeholder. */}
      <LibTable.CenterTd>
        <IconChip url={banner.promo_banner_icon} />
      </LibTable.CenterTd>

      {/* Title + description */}
      <LibTable.Td>
        <div className="min-w-0">
          <Link
            to={`/banners/edit/${banner.promo_banner_id}`}
            className="font-semibold text-navy no-underline hover:text-teal-green-dark block truncate"
            style={{ fontSize: 14, lineHeight: 1.25 }}
            title={banner.promo_banner_title || ''}
          >
            {banner.promo_banner_title || 'Untitled banner'}
          </Link>
          {banner.promo_banner_text && (
            <div
              className="text-text-muted truncate"
              style={{ fontSize: 12, marginTop: 2 }}
              title={banner.promo_banner_text}
            >
              {banner.promo_banner_text}
            </div>
          )}
        </div>
      </LibTable.Td>

      <LibTable.CenterTd>
        <StatusPill active={!inactive} />
      </LibTable.CenterTd>

      <LibTable.Td style={{ textAlign: 'right' }}>
        <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Link
            to={`/banners/edit/${banner.promo_banner_id}`}
            aria-label={`Edit ${banner.promo_banner_title || 'banner'}`}
          >
            <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
          </Link>
        </div>
      </LibTable.Td>
    </LibTable.Row>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function PromoBannersTableV2() {
  const dispatch = useDispatch();
  const banners = useSelector((s) => s.banners?.banners);

  // Local ordered list — source of truth for what's rendered + dragged.
  // Re-syncs from Redux on any banners change (covers refetch after a
  // sort failure, since updatePromoBannerOrder doesn't have a SUCCESS
  // case in the reducer).
  const [localList, setLocalList] = useState([]);

  useEffect(() => {
    dispatch(fetchAllPromoBanners());
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(banners)) return;
    setLocalList([...banners].sort(sortBySortNumber));
  }, [banners]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = localList.findIndex((b) => b.promo_banner_id === active.id);
      const newIndex = localList.findIndex((b) => b.promo_banner_id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const next = arrayMove(localList, oldIndex, newIndex);
      setLocalList(next);
      // Existing action expects a stringified array of ids.
      dispatch(
        updatePromoBannerOrder(
          JSON.stringify(next.map((b) => b.promo_banner_id)),
        ),
      );
    },
    [localList, dispatch],
  );

  if (!Array.isArray(banners)) {
    return (
      <div
        className="flex items-center justify-center text-text-muted"
        style={{ height: 256 }}
      >
        Loading banners…
      </div>
    );
  }

  const ids = localList.map((b) => b.promo_banner_id);
  const HINT = 'Drag any row to reorder · promo_banner_sort_number';

  return (
    <div style={{ padding: '0 28px 32px' }}>
      {localList.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
        >
          No banners yet — add one to get started.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="text-text-muted" style={{ fontSize: 11.5, margin: '0 4px 8px' }}>
            {HINT}
          </div>
          <LibTable
            fixed
            className="border border-sand"
            style={{ borderRadius: 12, overflow: 'hidden' }}
          >
            <colgroup>
              {/* drag handle */}
              <col style={{ width: 36 }} />
              {/* banner thumb */}
              <col style={{ width: 208 }} />
              {/* icon */}
              <col style={{ width: 80 }} />
              {/* title + text */}
              <col />
              {/* status */}
              <col style={{ width: '12%' }} />
              {/* actions */}
              <col style={{ width: '10%' }} />
            </colgroup>
            <LibTable.Head>
              <tr>
                <LibTable.Th aria-label="Drag to reorder"> </LibTable.Th>
                <LibTable.Th>Banner</LibTable.Th>
                <LibTable.CenterTh>Icon</LibTable.CenterTh>
                <LibTable.Th>Content</LibTable.Th>
                <LibTable.CenterTh>Status</LibTable.CenterTh>
                <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
              </tr>
            </LibTable.Head>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <tbody>
                {localList.map((b) => (
                  <SortablePromoBannerRow key={b.promo_banner_id} banner={b} />
                ))}
              </tbody>
            </SortableContext>
          </LibTable>
        </DndContext>
      )}
    </div>
  );
}
